import { defineComponent, computed, ref } from "vue";
import { Product, CartItem, Promotion, Service } from "@app/types/index";
import { getProductImageURL } from "@lemmonhq/blogic";
import { formatPrice } from "@lemmonhq/orders";
import {
  calculateProductPromotions,
  getPromotionBadgeText,
} from "@lemmonhq/orders";
import { PromotionInfoModal } from "../../components/PromotionInfoModal";
import { useMenu } from "@app/vue/providers/Menu";

export type MenuProductCardProps = {
  product: Product;
  cartItems: CartItem[];
  onProductSelect: Function;
  services: Service[];
  promotions: Promotion[];
  disableAdd?: boolean;
};

export const MenuProductCard = defineComponent<MenuProductCardProps>({
  name: "ProductCard",
  props: [
    "product",
    "cartItems",
    "onProductSelect",
    "services",
    "promotions",
    "disableAdd",
  ],
  setup(props) {
    const { product, services, promotions } = props;
    const { cart, shop } = useMenu();
    const showPromotionModal = ref(false);

    const selectedCartItem = computed(() => {
      const cartItems = cart.cart.items;
      let items = cartItems.filter((el) => el.productId === props.product.id);
      return items.reduce((sum, el) => sum + el.quantity, 0);
    });

    const promotionResult = computed(() => {
      if (!services || !promotions) {
        return {
          hasDiscountPromotions: false,
          totalDiscount: 0,
          finalPrice: parseFloat(product.price),
          originalPrice: parseFloat(product.price),
          nonDiscountPromotions: [],
          discountPromotions: [],
        };
      }
      const computedPromotions = calculateProductPromotions(
        services,
        product.id,
        parseFloat(product.price),
        1,
        promotions,
        cart.cart.serviceMethod,
      );
      return computedPromotions;
    });
    return () => {
      return (
        <div
          key={product.id}
          class="flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-lg"
          onClick={() => props.onProductSelect(product)}
        >
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-medium text-gray-900">{product.name}</h3>
            {product.description && product.description?.length > 0 ? (
              <p class="text-sm text-gray-500 font-light line-clamp-2 mt-0.5">{product.description}</p>
            ) : null}

            <div class="flex items-center gap-2 mt-1.5">
              {selectedCartItem.value > 0 ? (
                <button class="btn btn-xs font-bold btn-success">
                  {selectedCartItem.value}
                </button>
              ) : props.disableAdd ? null : (
                <button class="btn btn-xs btn-secondary">
                  +
                </button>
              )}
              <span class="text-sm text-gray-900">
                {promotionResult.value.hasDiscountPromotions ? (
                  <span class="flex items-center gap-2">
                    <span class="font-medium text-green-600">
                      {formatPrice(
                        promotionResult.value.finalPrice.toString(),
                        true,
                        2,
                        shop.value.currency,
                      )}
                    </span>
                    <span class="line-through text-xs text-gray-400">
                      {formatPrice(product.price, true, 2, shop.value.currency)}
                    </span>
                  </span>
                ) : (
                  formatPrice(product.price, true, 2, shop.value.currency)
                )}
              </span>

              {/* Non-discount promotion badge */}
              {promotionResult.value.nonDiscountPromotions.length > 0 && (
                <div class="flex items-center">
                  <button
                    class="badge badge-primary badge-sm cursor-pointer hover:badge-primary-focus"
                    onClick={(e) => {
                      e.stopPropagation();
                      showPromotionModal.value = true;
                    }}
                  >
                    {getPromotionBadgeText(
                      promotionResult.value.nonDiscountPromotions,
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          {product.image_list && product.image_list.length > 0 ? (
            <div class="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
              <img
                src={getProductImageURL(product, "thumbnail")}
                alt={product.name}
                class="w-full h-full object-cover"
              />
            </div>
          ) : null}

          {/* Promotion Info Modal */}
          {showPromotionModal.value && (
            <PromotionInfoModal
              promotions={promotionResult.value.nonDiscountPromotions}
              isOpen={showPromotionModal.value}
              onClose={() => (showPromotionModal.value = false)}
            />
          )}
        </div>
      );
    };
  },
});
